import { Form } from 'antd';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BiShield } from 'react-icons/bi';

import { CARD_NUMBER_PATTERN, CVC_PATTERN } from '@/utils/card';
import Dropdown from 'common/atoms/dropdown';
import Input from 'common/atoms/input';

import NoAvailablePayment from './NoAvailablePayment';

interface PaymentInfoFormProps {
  handlePaymentInfoInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaymentTypeChange: (value: string) => void;
  availablePaymentMethods: string[];
  hideZipAndAddress: boolean;
  paymentInfo: any;
  form: any;
}

const PaymentInfoForm: FC<PaymentInfoFormProps> = ({
  handlePaymentInfoInputChange,
  handlePaymentTypeChange,
  availablePaymentMethods,
  hideZipAndAddress,
  paymentInfo,
  form,
}) => {
  const { t } = useTranslation();
  const hasCreditCard = availablePaymentMethods.find((method) => method === 'credit_card') === undefined;
  if (hasCreditCard) return <NoAvailablePayment />;
  return (
    <div className="flex flex-wrap p-6 w-full justify-center">
      <Form form={form} className="w-full flex flex-wrap p-6">
        <div className="w-full">
          <Form.Item
            initialValue={paymentInfo.paymentType}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start', position: 'relative' }}
            name="paymentType"
            label={t('payment.paymentType')}
            rules={[{ required: true, message: t('payment.validation.paymentTypeRequired') }]}
          >
            <Dropdown
              onChange={handlePaymentTypeChange}
              options={[{ value: 'credit_card', label: t('payment.creditCard') }]}
              name={t('payment.paymentType')}
            />
          </Form.Item>
        </div>

        <div className="w-full">
          <Form.Item
            initialValue={paymentInfo.cardNumber}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start' }}
            name="cardNumber"
            label={t('payment.cardNumber')}
            rules={[
              { required: true, message: t('payment.validation.cardNumberRequired') },
              {
                pattern: CARD_NUMBER_PATTERN,
                message: t('payment.validation.invalidCard'),
              },
            ]}
          >
            <Input
              onChange={handlePaymentInfoInputChange}
              name="cardNumber"
              placeholder={t('payment.placeholders.cardNumber')}
            />
          </Form.Item>
        </div>

        <div className="w-full">
          <Form.Item
            initialValue={paymentInfo.nameOnTheCard}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start' }}
            name="nameOnTheCard"
            label={t('payment.nameOnCard')}
            rules={[
              { required: true, message: t('payment.validation.nameRequired') },
              { pattern: /^[\p{L}\p{M}\s'’.-]{2,30}$/u, message: t('payment.validation.nameInvalid') },
            ]}
          >
            <Input onChange={handlePaymentInfoInputChange} name="nameOnTheCard" placeholder={t('payment.nameOnCard')} />
          </Form.Item>
        </div>

        <div className="w-full">
          <Form.Item
            initialValue={paymentInfo.cvv}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 25 }}
            style={{ textAlign: 'start' }}
            name="cvc_cvv"
            label={t('payment.cvc')}
            rules={[
              { required: true, message: t('payment.validation.cvcRequired') },
              { pattern: CVC_PATTERN, message: t('payment.validation.cvcInvalid') },
            ]}
          >
            <Input onChange={handlePaymentInfoInputChange} name="cvv" placeholder={t('payment.placeholders.cvc')} />
          </Form.Item>
        </div>

        <div className="w-full">
          <Form.Item labelCol={{ span: 5 }} wrapperCol={{ span: 25 }} required label={t('payment.expirationDate')}>
            <div className="w-full flex flex-row items-start justify-start" dir="ltr">
              <Form.Item
                initialValue={paymentInfo.expireDateMonth}
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 25 }}
                style={{ textAlign: 'start' }}
                name="expireDateMonth"
                label=""
                rules={[
                  { required: true, message: t('payment.validation.monthRequired') },
                  { pattern: /^(0[1-9]|1[0-2])$/, message: t('payment.validation.month') },
                ]}
              >
                <Input
                  onChange={handlePaymentInfoInputChange}
                  name="expireDateMonth"
                  placeholder={t('payment.placeholders.mm')}
                />
              </Form.Item>
              <span className="p-2">:</span>
              <Form.Item
                initialValue={paymentInfo.expireDateYear}
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 25 }}
                style={{ textAlign: 'start' }}
                name="expireDateYear"
                label=""
                rules={[
                  { required: true, message: t('payment.validation.yearRequired') },
                  { pattern: /^(2[3-9]|3[0-9])$/, message: t('payment.validation.year') },
                ]}
              >
                <Input
                  onChange={handlePaymentInfoInputChange}
                  name="expireDateYear"
                  placeholder={t('payment.placeholders.yy')}
                />
              </Form.Item>
            </div>
          </Form.Item>
        </div>

        {!hideZipAndAddress && (
          <div className="w-full">
            <Form.Item
              initialValue={paymentInfo.address}
              labelCol={{ span: 5 }}
              wrapperCol={{ span: 25 }}
              style={{ textAlign: 'start' }}
              name="billingAddress"
              label={t('payment.billingAddress')}
              rules={[{ required: true, message: t('payment.validation.billingAddressRequired') }]}
            >
              <Input onChange={handlePaymentInfoInputChange} name="address" placeholder={t('payment.billingAddress')} />
            </Form.Item>
          </div>
        )}

        {!hideZipAndAddress && (
          <div className="w-full">
            <Form.Item
              initialValue={paymentInfo.zip}
              labelCol={{ span: 5 }}
              wrapperCol={{ span: 25 }}
              style={{ textAlign: 'start' }}
              name="ZIP"
              label={t('payment.zipCode')}
              rules={[
                { required: true, message: t('payment.validation.zipRequired') },
                { pattern: /^\d{5}$/, message: t('payment.validation.zipInvalid') },
              ]}
            >
              <Input onChange={handlePaymentInfoInputChange} name="zip" placeholder={t('payment.placeholders.zip')} />
            </Form.Item>
          </div>
        )}
        <div className="w-full flex items-center">
          {/* var(--main) color TLS notice text and shield icon */}
          <BiShield color="var(--main)" />
          <span style={{ color: 'var(--main)', marginInlineStart: '10px' }} className="font-medium text-sm">
            {t('payment.tlsNotice')}
          </span>
        </div>
      </Form>
    </div>
  );
};

export default PaymentInfoForm;
